/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2026 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { useContext } from "react";
import { getStudyAdvapacsViewerToken } from "../../pages/study/ducks/operators";
import AdvapacsViewerWindowContext from "../../context/AdvapacsViewerWindowContext";
import { AdvapacsViewerToken } from "../../types/integrations";
import { useFlag } from "@unleash/proxy-client-react";
import { isSafari, isIOS } from "react-device-detect";

const ADVAPACS_VIEWER_WINDOW_NAME = "AdvaView";

// ============================= HOOK ==============================
const useOpenAdvapacsViewer = (): ((studies: string[]) => Promise<void>) => {
  // ============================= CONTEXT ==============================
  const advapacsViewerWindow = useContext(AdvapacsViewerWindowContext);
  const enabledOpenNewTabInViewerWindow = useFlag(
    "open-new-tab-in-viewer-window"
  );

  // ============================= HELPERS ==============================
  const getUrl = (token: string, region: string): string => {
    // Region is resolved when the application first loads and cached in local storage.
    // There is no need to resolve it again.
    const localRegion = localStorage.getItem("region");

    if (!(region && localRegion)) {
      // eslint-disable-next-line no-console
      console.log(
        "[ADVAPACS VIEWER]: Cannot find the region in the cache and response."
      );
      return "";
    }

    const searchParams = new URLSearchParams({
      token,
      region: region || localRegion,
    });
    const baseUrl = `${window.location.origin}/Viewer/standard`;
    return `${baseUrl}?${searchParams.toString()}`;
  };

  const redirectToNewUrl = (windowUrl: string) => {
    advapacsViewerWindow.advapacsViewerWindow.location.href = windowUrl;
  };

  const openViewerWithNewWindow = (windowUrl: string) => {
    if (
      !advapacsViewerWindow.advapacsViewerWindow ||
      advapacsViewerWindow.advapacsViewerWindow?.closed
    ) {
      if (
        enabledOpenNewTabInViewerWindow &&
        window.name === ADVAPACS_VIEWER_WINDOW_NAME
      ) {
        // This code is running inside the viewer window itself (e.g. the
        // viewer redirected to a studies page and the user opened another
        // study from there). Give up the shared name so window.open below
        // creates a genuinely new window instead of navigating this one in
        // place, and so this now-stale window can't later be hijacked by
        // another window.open(url, "AdvaView") call made from elsewhere.
        window.name = "";
      }
      advapacsViewerWindow.advapacsViewerWindow = window.open(
        windowUrl,
        ADVAPACS_VIEWER_WINDOW_NAME
      );
    } else {
      redirectToNewUrl(windowUrl);
    }
  };

  const openEmptyWindow = (): Promise<void> => {
    if (!(isSafari || isIOS)) {
      // prevent open new empty window if browser is not safari or iOS
      // eslint-disable-next-line no-console
      console.log(
        "[ADVAPACS VIEWER]: not allow open new empty window for when browser is not safari and not iOS device."
      );
      return Promise.resolve();
    }
    // validate window
    if (
      !advapacsViewerWindow.advapacsViewerWindow ||
      advapacsViewerWindow.advapacsViewerWindow?.closed
    ) {
      openViewerWithNewWindow("");
    } else {
      // eslint-disable-next-line no-console
      console.log("[ADVAPACS VIEWER]: This window already opened.");
    }
    return new Promise<void>((resolve) => {
      setTimeout(resolve, 500);
    });
  };

  // ============================= FUNCTIONS ==============================
  const openViewer = (token: AdvapacsViewerToken) => {
    if (!token?.token) {
      // eslint-disable-next-line no-console
      console.log("[ADVAPACS VIEWER]: can't open window without token.");
      return;
    }
    const windowUrl = getUrl(token.token, token?.region as string);
    if (!windowUrl) return;
    if (isSafari || isIOS) {
      redirectToNewUrl(windowUrl);
    } else {
      openViewerWithNewWindow(windowUrl);
    }
  };

  // ============================= ROOT ==============================
  return async (studies: string[]) => {
    try {
      await openEmptyWindow();
      const token = await getStudyAdvapacsViewerToken(studies);
      openViewer(token?.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };
};

export default useOpenAdvapacsViewer;
